<template>
  <v-data-table
  :headers="headers"
  :items="tutorials"
  must-sort
  sort-by="data.title"
  dense
  disable-pagination
  hide-default-footer
  >
    <template v-slot:top>
      <v-toolbar flat color="white">
        <v-spacer></v-spacer>

        <v-dialog v-model="dialogVisible" max-width="600px" @click:outside="resetForm($refs.form)">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              color="primary"
              small
              depressed
            >
              <v-icon small left>add</v-icon><span>Tutorial</span>
            </v-btn>
          </template>
          <v-card outlined>
            <v-form ref="form">
              <v-card-title>
                Add a new tutorial
              </v-card-title>

              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col cols="12">
                      <v-text-field v-model="title" label="Title" :rules="[inputRules.required]" autofocus></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <div class="mt-4 mb-4">
                        Copy and paste your exercises from MS Word, Google Docs, PDF, webpage etc. into the text area below. Review and correct any formatting issues using the controls in the toolbar.
                      </div>
                      <tiptap-vuetify
                        id="tutorial-html"
                        v-model="description"
                        placeholder="Type or paste content here..."
                        :extensions="extensions"
                        :card-props="{ outlined: true, height: 400,  overflow: 'auto'}"/>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>

              <v-card-actions>
                <v-spacer />
                <v-btn depressed @click="add" :loading="submitting">Add</v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-dialog>
      </v-toolbar>


    <v-dialog v-if="editItem" v-model="editDialogVisible" max-width="600px" @click:outside="resetForm($refs.editForm)">
      <v-card outlined>
        <v-form ref="editForm">
          <v-card-title>
            Edit tutorial
          </v-card-title>

          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-text-field v-model="title" label="Title" :rules="[inputRules.required]" autofocus></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <div class="mt-4 mb-4">
                    Copy and paste your exercises from MS Word, Google Docs, PDF, webpage etc. into the text area below. Review and correct any formatting issues using the controls in the toolbar.
                  </div>
                  <tiptap-vuetify
                    id="tutorial-html"
                    v-model="description"
                    :extensions="extensions"
                    :card-props="{ outlined: true, height: 400,  overflow: 'auto'}"/>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn depressed @click="update" :loading="submitting">Update</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    </template>

    <template v-slot:item.actions="{ item }">

      <v-icon
        small
        class="mr-2"
        @click="showEditDialog(item)"
      >
        edit
      </v-icon>

      <v-icon
        small
        @click="archiveItem(item)"
      >
        delete
      </v-icon>
    </template>

  </v-data-table>
</template>

<script>
import { TiptapVuetify, Heading, Bold, Italic, Underline, Code, Paragraph, BulletList, OrderedList, ListItem, Link, Blockquote, HorizontalRule, History } from 'tiptap-vuetify'
import { mapGetters } from 'vuex'
import firebase from '@/firebase.config'

export default {
  name: "Tutorials",
  props: ['subject'],
  components: { TiptapVuetify },
  data: function () {
    return {
      dialogVisible: false,
      submitting: false,
      editDialogVisible: false,
      editItem: null,
      title: '',
      description: null,
      headers: [
        { text: 'Tutorials', value: 'data.title'},
        { text: 'Actions', value: 'actions', sortable: false, align: 'end'}
      ],
      inputRules: {
        required: value => !!value || 'Required'
      },
      extensions: [
        History,
        Blockquote,
        Link,
        Underline,
        Italic,
        ListItem,
        BulletList,
        OrderedList,
        [Heading, {
          options: {
            levels: [1, 2, 3]
          }
        }],
        Bold,
        Code,
        HorizontalRule,
        Paragraph
      ],
    }
  },
  methods: {
    async add() {
      if (this.$refs.form.validate()) {
        this.submitting = true
        // Process description to make sure all links have target="_BLANK"
        const tutorialData = { 
          title: this.title,
          description: this.description.split('<a href=').join('<a target="_BLANK" href='),
          isArchived: false,
          parentPath: this.subject.path
        }
        await this.$store.dispatch('addTutorial', tutorialData)

        // If this is the first time creating a tutorial update their completeSteps
        if (!this.lecturer.data.completedSteps.includes('CREATE_TUTORIAL')) {
          this.lecturer.data.completedSteps.push('CREATE_TUTORIAL')
          await this.$store.dispatch('updateLecturer', this.lecturer)
        }
        
        // Report to GA
        firebase.analytics().logEvent('add_tutorial')
        
        this.resetForm(this.$refs.form)
        this.submitting = false
        this.dialogVisible = false
      }
    },
    showEditDialog(item) {
      this.editItem = item
      // Copy from doc to local variables so
      // changes can be reverted
      this.title = item.data.title
      this.description = item.data.description
      this.editDialogVisible = true
    },
    async update() {
      if (this.$refs.editForm.validate()) {
        this.submitting = true

        // Copy changes from local variables to the object
        this.editItem.data.title = this.title
        this.editItem.data.description = this.description.split('<a href=').join('<a target="_BLANK" href=')
        // Update Firestore
        await this.$store.dispatch('updateTutorial', this.editItem)
        
        this.resetForm(this.$refs.editForm)
        this.submitting = false
        this.editDialogVisible = false
      }
    },
    async archiveItem(item) {
      const res = await this.$confirm('Are you sure you want to archive "' + item.data.title + '"?')
      if (res === true) {
        item.data.isArchived = true
        await this.$store.dispatch('updateTutorial', item)
      }
    },
    resetForm(form) {
      // Normal form reset does not reset the description tiptap editor
      form.reset()
      this.description = null
    }
  },
  computed: {
    ...mapGetters({
      lecturer: 'lecturer'
    }),
    tutorials() {
      let tutorials = []
      if (this.subject.tutorials) {
        tutorials = this.subject.tutorials.filter(tut => tut.data.isArchived === false)
      }
      return tutorials
    }
  }
}
</script>

<style>
.ProseMirror {
  height: 350px;
  overflow-y: auto;
}
</style>
